import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import { alpha } from '@mui/material/styles';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';

import Iconify from '../../theme/iconify/iconify';

import CourseCard from './CourseCard';
import NewCourse from './modal/NewCourse';
import { mislecAPI } from '../../util/axios';
import Notification from '../util/Notification';

function Courses({ user, updateUser }) {
    const [courses, setCourses] = React.useState(null);
    const [open, setOpen] = React.useState(false);
    const [message, setMessage] = React.useState(null);
    const [severity, setSeverity] = React.useState(null);
    const [courseModal, setCourseModal] = React.useState(false);

    const { t } = useTranslation();
    const navigate = useNavigate();

    React.useEffect(() => {
        fetchCourses();
    }, [user]);

    const createCourse = (title) => {
        if (title === '') {
            setOpen(true);
            setMessage(t('course.error.course.title'));
            setSeverity('warning');
            return;
        }

        //  Timestamp
        const timestamp = new Date().toISOString().replace(/T/, ' ').replace(/\..+/, '');

        //  Request body
        const data = {
            title: title,
            user: user._id,
            timestamp: timestamp,
            sections: []
        }

        //  Send data to server
        mislecAPI.post(`/api/course`, data)
            .then(res => {
                //  Fetch courses
                fetchCourses();

                //  Notify user of success
                setOpen(true);
                setMessage(t(res.data.detail));
                setSeverity('success');
                setCourseModal(false);

                setTimeout(() => {
                    //  Redirect to the new course
                    navigate(`/course/${res.data.id}`);
                    updateUser(null)
                }, 2500);
            })
            .catch(error => {
                //  Notify user of error
                setMessage(error.response ? t(error.response.data.detail) : t('api.general.500'));
                setSeverity('error');
                setOpen(true);

                //  Expired session
                if (error.response)
                    if (error.response.data.detail === 'api.auth.verify.401')
                        updateUser(null);
            });
    }

    const importCourse = (course) => {
        //  Data preparation
        course.user = user._id;
        course.timestamp = new Date().toISOString().replace(/T/, ' ').replace(/\..+/, '');
        course.published = false;

        //  Send data to server
        mislecAPI.post(`/api/course`, course)
            .then(res => {
                //  Fetch courses
                fetchCourses();

                //  Notify user of success
                setOpen(true);
                setMessage(t(res.data.detail));
                setSeverity('success');
                setCourseModal(false);

                setTimeout(() => {
                    //  Redirect to the new course
                    navigate(`/course/${res.data.id}`);
                    updateUser(null);
                }, 2500);
            })
            .catch(error => {
                //  Notify user of error
                setMessage(error.response ? t(error.response.data.detail) : t('api.general.500'));
                setSeverity('error');
                setOpen(true);

                //  Expired session
                if (error.response)
                    if (error.response.data.detail === 'api.auth.verify.401')
                        updateUser(null);
            });
    }

    const fetchCourses = async () => {
        if (!user) return;

        //  Fetch courses from the server
        mislecAPI.get(`/api/courses`)
            .then(res => {
                setCourses(res.data.courses);
            })
            .catch(error => {
                //  Notify user of error
                setMessage(error.response ? t(error.response.data.detail) : t('api.general.500'));
                setSeverity('error');
                setOpen(true);

                //  Expired session
                if (error.response)
                    if (error.response.data.detail === 'api.auth.verify.401')
                        updateUser(null);
            });
    }

    const joinCourse = async (courseId) => {
        if (!user) return

        mislecAPI.post(`/api/course/join`, {
            id: courseId,
            enrolled_at: new Date().toISOString().slice(0, 19).replace('T', ' ')
        }).then((res) => {
            //  Notify user of success
            setMessage(t(res.data.detail));
            setSeverity('success');
            setOpen(true);

            //  Fetch courses
            fetchCourses();
            updateUser(res.data.user);
        }).catch((error) => {
            //  Notify user of error
            setMessage(error.response ? t(error.response.data.detail) : t('api.general.500'));
            setSeverity('error');
            setOpen(true);

            //  Expired session
            if (error.response)
                if (error.response.data.detail === 'api.auth.verify.401')
                    updateUser(null);
        })
    }

    React.useEffect(() => {
        if (courses) return;
        fetchCourses();
    }, [courses]);

    return (
        <>
            <Container>
                <Stack direction='row' alignItems='center' justifyContent='space-between' mb={5}>
                    <Typography variant='h4'>{t('courses.title')}</Typography>

                    {(user.role === 'admin' || user.role === 'professor') &&
                        <Button
                            variant=''
                            startIcon={<Iconify icon='mdi:book-plus-outline' />}
                            onClick={() => setCourseModal(true)}
                            sx={{
                                borderRadius: '8px !important',
                                backgroundColor: 'rgba(33, 43, 54, 0.08)',
                                '&:hover': {
                                    backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08),
                                    color: 'primary.main'
                                }
                            }}
                        >
                            {t('courses.newCourseButton')}
                        </Button>
                    }
                </Stack>
                <Stack mb={5}>
                    <Grid container spacing={3}>
                        {courses?.length > 0 &&
                            <>
                                {user.role === 'admin' &&
                                    <>
                                        {courses.filter(course => course.user === user._id).length > 0 &&
                                            <Grid item xs={12}>
                                                <Typography variant='h5'>{t('courses.userCourses')}</Typography>
                                            </Grid>
                                        }
                                        {courses.filter(course => course.user === user._id).map(courseItem => (
                                            <CourseCard key={courseItem._id} access={true} course={courseItem} />
                                        ))}
                                        <Grid item xs={12}>
                                            <Typography variant='h5'>{t('courses.allCourses')}</Typography>
                                        </Grid>
                                        {courses.filter(course => course.user !== user._id).map(courseItem => (
                                            <CourseCard key={courseItem._id} access={true} course={courseItem} />
                                        ))}
                                    </>
                                }
                                {user.role === 'professor' &&
                                    <>
                                        {courses.filter(course => course.user === user._id).length > 0 &&
                                            <Grid item xs={12}>
                                                <Typography variant='h5'>{t('courses.userCourses')}</Typography>
                                            </Grid>
                                        }
                                        {courses.filter(course => course.user === user._id).map(courseItem => (
                                            <CourseCard key={courseItem._id} access={true} course={courseItem} />
                                        ))}
                                        <Grid item xs={12}>
                                            <Typography variant='h5'>{t('courses.allCourses')}</Typography>
                                        </Grid>
                                        {courses.filter(course => course.user !== user._id).map(courseItem => (
                                            <CourseCard key={courseItem._id} access={false} course={courseItem} />
                                        ))}
                                    </>
                                }
                                {user.role === 'student' &&
                                    <>
                                        {courses.filter(course => new Set(user?.courses?.filter(course => course.enrolled).map(course => course.id.toString())).has(course._id)).length > 0 &&
                                            <Grid item xs={12}>
                                                <Typography variant='h5'>{t('courses.userCourses')}</Typography>
                                            </Grid>
                                        }
                                        {courses.filter(course => new Set(user?.courses?.filter(course => course.enrolled).map(course => course.id.toString())).has(course._id)).map(courseItem => (
                                            <CourseCard key={courseItem._id} access={true} course={courseItem} />
                                        ))}
                                        {courses.filter(course => new Set(user?.courses?.filter(course => !course.enrolled).map(course => course.id.toString())).has(course._id)).length > 0 &&
                                            <Grid item xs={12}>
                                                <Typography variant='h5'>{t('courses.pendingCourses')}</Typography>
                                            </Grid>
                                        }
                                        {courses.filter(course => new Set(user?.courses?.filter(course => !course.enrolled).map(course => course.id.toString())).has(course._id)).map(courseItem => (
                                            <CourseCard key={courseItem._id} access={false} course={courseItem} />
                                        ))}
                                        {courses.filter(course => !new Set(user?.courses?.map(course => course.id.toString())).has(course._id)).length > 0 &&
                                            <Grid item xs={12}>
                                                <Typography variant='h5'>{t('courses.allCourses')}</Typography>
                                            </Grid>
                                        }
                                        {courses.filter(course => !new Set(user?.courses?.map(course => course.id.toString())).has(course._id)).map(courseItem => (
                                            <CourseCard key={courseItem._id} access={false} course={courseItem} enrollInCourse={joinCourse} />
                                        ))}
                                    </>
                                }
                            </>
                        }
                        {courses?.length === 0 &&
                            <Grid item xs={12}>
                                <Typography>{t('courses.noCourses')}</Typography>
                            </Grid>
                        }
                        {!courses &&
                            <Grid item xs={12}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '700px' }}>
                                    <CircularProgress />
                                </Box>
                            </Grid>
                        }
                    </Grid>
                </Stack>
            </Container>
            <NewCourse createCourse={createCourse} importCourse={importCourse} courseModal={courseModal} setCourseModal={setCourseModal} setOpen={setOpen} setMessage={setMessage} setSeverity={setSeverity} />
            <Notification open={open} message={message} severity={severity} onClose={() => setOpen(false)} />
        </>
    );
}

export default Courses;